import React, { useEffect, useState } from 'react'

import ErrorBoundary from '../../error-boundary'
import ScrollView, { type ScrollViewProps } from '../scroll-view'
import {
  coloring,
  composeGroups,
  createView,
  flexContainer,
  flexItem,
  sizing
} from '../_support'

/* -----------------------------------------------------------------------------
 * Screen
 *
 * A window-high scrolling page on the light gray canvas, as the login and
 * password screens use it. Flex container props go to the inner area, the
 * rest to the ScrollView.
 * -------------------------------------------------------------------------- */

// react-native-safe-area-context's view; the insets are zero on a desktop
const SafeArea = createView({
  displayName: 'SafeArea',
  groups: composeGroups(coloring, flexContainer, flexItem, sizing)
})

const safeAreaPropKeys = new Set([
  'emulateUnlessSupported',
  'forceInset',
  ...Object.keys(flexContainer)
])

const useWindowHeight = () => {
  const [height, setHeight] = useState(window.innerHeight)
  useEffect(() => {
    const update = () => setHeight(window.innerHeight)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  return height
}

export type ScreenProps = ScrollViewProps & {
  insetTop?: 'always' | 'never'
  insetBottom?: 'always' | 'never'
}

const Screen = ({ children, insetTop, insetBottom, ...props }: ScreenProps) => {
  const height = useWindowHeight()
  const scrollViewProps: Record<string, any> = {
    height,
    bg: 'shade01',
    contentContainerStyle: { flexGrow: 1 }
  }
  const safeAreaProps: Record<string, any> = {}
  Object.keys(props).forEach(prop => {
    if (safeAreaPropKeys.has(prop)) safeAreaProps[prop] = props[prop]
    else scrollViewProps[prop] = props[prop]
  })
  delete safeAreaProps.forceInset
  delete safeAreaProps.emulateUnlessSupported

  return (
    <ScrollView flex='1 1 auto' {...scrollViewProps}>
      <SafeArea flex='1 1 auto' {...safeAreaProps}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </SafeArea>
    </ScrollView>
  )
}

export default Screen
